import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Squelette générique affiché pendant le chargement d'une page ou d'une liste.
export const PageSkeleton = ({ rows = 8, title = true }) => (
  <div className="animate-pulse space-y-4 p-1" data-testid="page-skeleton" aria-busy="true" aria-label="Chargement">
    {title && <div className="h-8 w-56 rounded-lg bg-slate-200/80 dark:bg-slate-700/60" />}
    <div className="flex gap-2"><div className="h-9 w-40 rounded-lg bg-slate-200/70 dark:bg-slate-700/50" /><div className="h-9 w-32 rounded-lg bg-slate-200/70 dark:bg-slate-700/50" /></div>
    <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/50 overflow-hidden">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
          <div className="h-8 w-8 rounded-full bg-slate-200/80 dark:bg-slate-700/60" />
          <div className="h-3 rounded bg-slate-200/80 dark:bg-slate-700/60" style={{ width: `${30 + (i * 17) % 40}%` }} />
          <div className="ml-auto h-3 w-16 rounded bg-slate-200/60 dark:bg-slate-700/40" />
        </div>
      ))}
    </div>
  </div>
);

// Barre de pagination numérotée (50 lignes par défaut côté serveur).
export const Pagination = ({ page, pages, total, pageSize = 50, onChange, testId = 'pagination' }) => {
  if (!total && !pages) return null;
  const p = Number(page) || 1, n = Math.max(1, Number(pages) || 1);
  const from = total ? (p - 1) * pageSize + 1 : 0, to = total ? Math.min(total, p * pageSize) : 0;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-300" data-testid={testId}>
      <span data-testid={`${testId}-info`}>{total ? `${from}–${to} sur ${total}` : 'Aucun résultat'}</span>
      <div className="ml-auto flex items-center gap-1">
        <Button size="sm" variant="outline" disabled={p <= 1} onClick={() => onChange(p - 1)} aria-label="Page précédente" data-testid={`${testId}-prev`}><ChevronLeft className="w-4 h-4" /></Button>
        <span className="px-2 font-semibold" data-testid={`${testId}-page`}>{p} / {n}</span>
        <Button size="sm" variant="outline" disabled={p >= n} onClick={() => onChange(p + 1)} aria-label="Page suivante" data-testid={`${testId}-next`}><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

// Valeur retardée (recherche avec délai).
export const useDebounced = (value, delay = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return v;
};
